import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { getBackendUrl } from '../../utils/api';

const API_URL = getBackendUrl() || '';

export function CampaignModal({ segments, token, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    segment_ids: [],
    daily_limit: 50,
    start_hour: 9,
    end_hour: 21,
    allowed_days: [0, 1, 2, 3, 4],
    min_interval: 30,
    max_interval: 90,
    opening_messages: ['', '', ''],
    followup_messages: ['']
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleSegment = (segmentId) => {
    setFormData(prev => ({
      ...prev,
      segment_ids: prev.segment_ids.includes(segmentId)
        ? prev.segment_ids.filter(id => id !== segmentId)
        : [...prev.segment_ids, segmentId]
    }));
  };

  const updateMessage = (type, index, value) => {
    setFormData(prev => {
      const arr = [...prev[type]];
      arr[index] = value;
      return { ...prev, [type]: arr };
    });
  };

  const addMessage = (type) => {
    setFormData(prev => ({ ...prev, [type]: [...prev[type], ''] }));
  };

  const removeMessage = (type, index) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      allowed_days: prev.allowed_days.includes(day)
        ? prev.allowed_days.filter(d => d !== day)
        : [...prev.allowed_days, day].sort()
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validOpenings = formData.opening_messages.filter(m => m.trim());
    if (!formData.name || formData.segment_ids.length === 0) {
      setError('Preencha o nome e selecione pelo menos um segmento');
      return;
    }
    if (validOpenings.length === 0) {
      setError('Adicione pelo menos 1 mensagem de abertura');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        opening_messages: validOpenings,
        followup_messages: formData.followup_messages.filter(m => m.trim())
      };

      const response = await fetch(`${API_URL}/api/prospection/campaigns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail);

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-gray-900 z-10">
          <h3 className="text-xl font-semibold text-white">Nova Campanha</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-500/20 text-red-300 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm text-gray-400 mb-1">Nome da Campanha *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              placeholder="Ex: Prospecção Hamburguerias"
              data-testid="campaign-name-input"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Descrição</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Segmentos *</label>
            <div className="space-y-2">
              {segments.map(seg => (
                <label key={seg.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.segment_ids.includes(seg.id)}
                    onChange={() => toggleSegment(seg.id)}
                    className="rounded border-gray-600 bg-gray-800 text-blue-500"
                  />
                  <span className="text-white">{seg.name}</span>
                </label>
              ))}
              {segments.length === 0 && (
                <p className="text-gray-500 text-sm">Crie segmentos primeiro</p>
              )}
            </div>
          </div>

          {/* Mensagens de Abertura */}
          <div className="border border-green-500/30 rounded-lg p-4 bg-green-500/5">
            <label className="block text-sm font-medium text-green-400 mb-1">
              Mensagens de Abertura * (variações)
            </label>
            <p className="text-xs text-gray-500 mb-3">
              O sistema escolhe aleatoriamente entre as variações. Use {'{nome}'} para o nome do estabelecimento.
            </p>
            {formData.opening_messages.map((msg, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <span className="text-green-400 text-sm mt-2 w-6 flex-shrink-0">{i + 1}.</span>
                <textarea
                  value={msg}
                  onChange={(e) => updateMessage('opening_messages', i, e.target.value)}
                  className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm resize-none"
                  rows={2}
                  placeholder={
                    i === 0 ? "Ex: Olá! Tudo bem? Vocês trabalham com delivery?" :
                    i === 1 ? "Ex: Oi! Vi que vocês são do ramo de alimentação. Fazem delivery?" :
                    "Ex: Boa tarde! Vocês fazem entrega? Estou buscando parceiros."
                  }
                  data-testid={`opening-msg-${i}`}
                />
                {formData.opening_messages.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeMessage('opening_messages', i)}
                    className="text-red-400 hover:text-red-300 p-1"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            ))}
            {formData.opening_messages.length < 5 && (
              <button
                type="button"
                onClick={() => addMessage('opening_messages')}
                className="text-green-400 hover:text-green-300 text-sm flex items-center gap-1 mt-1"
              >
                <Plus size={14} /> Adicionar variação
              </button>
            )}
          </div>

          {/* Mensagens de Follow-up */}
          <div className="border border-blue-500/30 rounded-lg p-4 bg-blue-500/5">
            <label className="block text-sm font-medium text-blue-400 mb-1">
              Follow-up (após resposta)
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Enviada automaticamente quando o lead responde a mensagem de abertura. Deixe em branco para desativar.
            </p>
            {formData.followup_messages.map((msg, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <span className="text-blue-400 text-sm mt-2 w-6 flex-shrink-0">{i + 1}.</span>
                <textarea
                  value={msg}
                  onChange={(e) => updateMessage('followup_messages', i, e.target.value)}
                  className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm resize-none"
                  rows={2}
                  placeholder="Ex: Que legal! Podem me enviar o cardápio? Tenho uma proposta interessante para vocês."
                  data-testid={`followup-msg-${i}`}
                />
                {formData.followup_messages.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeMessage('followup_messages', i)}
                    className="text-red-400 hover:text-red-300 p-1"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            ))}
            {formData.followup_messages.length < 3 && (
              <button
                type="button"
                onClick={() => addMessage('followup_messages')}
                className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 mt-1"
              >
                <Plus size={14} /> Adicionar variação
              </button>
            )}
          </div>

          {/* Configurações de envio */}
          <div className="border border-purple-500/30 rounded-lg p-4 bg-purple-500/5 space-y-4">
            <label className="block text-sm font-medium text-purple-400">
              Configurações de Envio
            </label>

            {/* Horário */}
            <div>
              <p className="text-xs text-gray-400 mb-2">Horário de Funcionamento</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Início</label>
                  <input
                    type="time"
                    value={`${String(formData.start_hour).padStart(2,'0')}:00`}
                    onChange={(e) => handleChange('start_hour', parseInt(e.target.value.split(':')[0]) || 9)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Fim</label>
                  <input
                    type="time"
                    value={`${String(formData.end_hour).padStart(2,'0')}:00`}
                    onChange={(e) => handleChange('end_hour', parseInt(e.target.value.split(':')[0]) || 21)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Dias da semana */}
            <div>
              <p className="text-xs text-gray-400 mb-2">Dias da Semana</p>
              <div className="flex gap-2 flex-wrap">
                {['Seg','Ter','Qua','Qui','Sex','Sáb','Dom'].map((day, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleDay(i)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      formData.allowed_days.includes(i)
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>

            {/* Limite diário */}
            <div>
              <p className="text-xs text-gray-400 mb-1">
                Limite Diário: <span className="text-white font-medium">{formData.daily_limit} mensagens</span>
              </p>
              <input
                type="range"
                min={5} max={200} step={5}
                value={formData.daily_limit}
                onChange={(e) => handleChange('daily_limit', parseInt(e.target.value))}
                className="w-full accent-purple-500"
              />
              <div className="flex justify-between text-xs text-gray-600">
                <span>5</span><span>200</span>
              </div>
            </div>

            {/* Intervalos */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-400 mb-1">
                  Intervalo Mín: <span className="text-white font-medium">{formData.min_interval}s</span>
                </p>
                <input
                  type="range"
                  min={10} max={300} step={5}
                  value={formData.min_interval}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    handleChange('min_interval', val);
                    if (val > formData.max_interval) handleChange('max_interval', val);
                  }}
                  className="w-full accent-purple-500"
                />
                <div className="flex justify-between text-xs text-gray-600">
                  <span>10s</span><span>300s</span>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">
                  Intervalo Máx: <span className="text-white font-medium">{formData.max_interval}s</span>
                </p>
                <input
                  type="range"
                  min={10} max={300} step={5}
                  value={formData.max_interval}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    handleChange('max_interval', val);
                    if (val < formData.min_interval) handleChange('min_interval', val);
                  }}
                  className="w-full accent-purple-500"
                />
                <div className="flex justify-between text-xs text-gray-600">
                  <span>10s</span><span>300s</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              O intervalo entre cada mensagem será aleatório entre {formData.min_interval}s e {formData.max_interval}s para evitar banimento.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg font-medium"
              data-testid="campaign-submit-btn"
            >
              {loading ? 'Criando...' : 'Criar Campanha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
